import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock3, Video, MapPin, AlertCircle,
  ShieldCheck, Timer, Video as VideoIcon,
} from 'lucide-react';
import { whatsappUrl } from '@/config/site';

/* ─── Formulário de solicitação ──────────────────────────
   Vai para o mesmo CRM que a equipe já usa (POST /api/portal/treinamentos).
   Não fala com o GoTo diretamente — isso acontece quando o suporte confirma
   o pedido do lado de dentro do CRM. */

type Modalidade = 'remoto' | 'presencial';
type Status = 'idle' | 'erro';

const TEMAS_SUGERIDOS = [
  'Fechamento de caixa',
  'Cadastro de produto',
  'Conciliação financeira',
  'Luk IA',
  'Entrada de nota fiscal',
];

function TrustCard() {
  return (
    <div className="flex flex-col gap-5 rounded-[28px] border border-slate-100 bg-slate-50/60 p-7">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#7f00ff]/10 text-[#7f00ff]">
          <VideoIcon size={20} />
        </div>
        <div>
          <p className="text-[13px] font-extrabold text-[#0f172a]">Sessão pelo GoTo Meeting</p>
          <p className="text-[12px] text-slate-500">Ou presencial, se preferir</p>
        </div>
      </div>

      <div className="h-px bg-slate-200/70" />

      {[
        { icon: Timer, text: 'Confirmação em até 1 dia útil' },
        { icon: ShieldCheck, text: 'Sem custo adicional para clientes ativos' },
        { icon: Calendar, text: 'Você escolhe o dia e a gente encaixa o instrutor' },
      ].map(({ icon: Icon, text }) => (
        <div key={text} className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-[#7f00ff] shadow-sm">
            <Icon size={15} />
          </div>
          <span className="text-[13px] font-medium text-slate-600">{text}</span>
        </div>
      ))}
    </div>
  );
}

function TrainingRequestForm() {
  const [cliente, setCliente] = useState('');
  const [contato, setContato] = useState('');
  const [tema, setTema] = useState('');
  const [modalidade, setModalidade] = useState<Modalidade>('remoto');
  const [dia, setDia] = useState('');
  const [hora, setHora] = useState('');
  const [observacao, setObservacao] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [mensagemErro, setMensagemErro] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cliente.trim()) {
      setStatus('erro');
      setMensagemErro('Informe seu nome ou o nome da empresa.');
      return;
    }

    const linhas = [
      'Olá! Gostaria de agendar um treinamento.',
      `Nome/empresa: ${cliente.trim()}`,
      contato.trim() && `Contato: ${contato.trim()}`,
      tema.trim() && `Assunto: ${tema.trim()}`,
      `Modalidade: ${modalidade}`,
      dia && `Dia preferido: ${dia}`,
      hora && `Horário: ${hora}`,
      observacao.trim() && `Observação: ${observacao.trim()}`,
    ].filter(Boolean);

    window.open(whatsappUrl(linhas.join('\n')), '_blank');
    setStatus('idle');
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-bold text-slate-500">Seu nome ou empresa *</label>
          <input
            value={cliente}
            onChange={(e) => setCliente(e.target.value)}
            placeholder="Posto Exemplo Ltda."
            className="rounded-xl border border-slate-200 px-4 py-3 text-[13.5px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-bold text-slate-500">Telefone ou e-mail</label>
          <input
            value={contato}
            onChange={(e) => setContato(e.target.value)}
            placeholder="(11) 99999-0000"
            className="rounded-xl border border-slate-200 px-4 py-3 text-[13.5px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-bold text-slate-500">Assunto do treinamento</label>
        <input
          value={tema}
          onChange={(e) => setTema(e.target.value)}
          placeholder="Fechamento de caixa, cadastro de produto..."
          list="temas-sugeridos"
          className="rounded-xl border border-slate-200 px-4 py-3 text-[13.5px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
        />
        <datalist id="temas-sugeridos">
          {TEMAS_SUGERIDOS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
      </div>

      <div className="flex gap-2">
        {(['remoto', 'presencial'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModalidade(m)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12.5px] font-bold transition-colors ${
              modalidade === m
                ? 'border-[#7f00ff] bg-[#7f00ff] text-white'
                : 'border-slate-200 text-slate-500 hover:border-slate-300'
            }`}
          >
            {m === 'remoto' ? <Video size={14} /> : <MapPin size={14} />}
            {m === 'remoto' ? 'Remoto' : 'Presencial'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-bold text-slate-500">Dia preferido</label>
          <div className="relative">
            <Calendar size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="date"
              value={dia}
              onChange={(e) => setDia(e.target.value)}
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-[13px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-bold text-slate-500">Horário</label>
          <div className="relative">
            <Clock3 size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-3 text-[13px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-[12px] font-bold text-slate-500">Observação (opcional)</label>
        <textarea
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          rows={2}
          placeholder="Algo específico que a equipe precisa saber?"
          className="resize-none rounded-xl border border-slate-200 px-4 py-3 text-[13.5px] text-slate-800 outline-none transition-colors focus:border-[#7f00ff]/50 focus:ring-2 focus:ring-[#7f00ff]/10"
        />
      </div>

      <AnimatePresence>
        {status === 'erro' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-[12.5px] font-medium text-red-600"
          >
            <AlertCircle size={15} className="shrink-0" />
            {mensagemErro}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="submit"
        className="mt-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#7f00ff] px-8 py-4 text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_10px_30px_-8px_rgba(127, 0, 255,0.45)] transition-all hover:bg-[#6b00d7] hover:shadow-[0_16px_40px_-10px_rgba(127, 0, 255,0.55)] active:scale-95"
      >
        Solicitar treinamento
      </button>
    </form>
  );
}

/* ─── Main Component ────────────────────────────────── */

export default function LukosAppStoreSection() {
  return (
    <section className="relative w-full overflow-hidden bg-white py-20 md:py-32">
      {/* Subtle background glow */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/2 left-[10%] w-[600px] h-[600px] bg-purple-100/30 rounded-full blur-[140px] -translate-y-1/2" />
        <div className="absolute top-1/2 right-[5%] w-[400px] h-[400px] bg-indigo-50/40 rounded-full blur-[120px] -translate-y-1/2" />
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2 lg:gap-24">

          {/* ── LEFT: contexto + confiança ─────────────── */}
          <div className="order-2 flex flex-col gap-8 lg:order-1 lg:sticky lg:top-28">
            <div className="flex flex-col gap-5">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#7f00ff]">
                Treinamento para clientes
              </p>
              <h2 className="text-[36px] md:text-[44px] font-extrabold text-[#0f172a] leading-[1.05] tracking-tight">
                Agende o treinamento{' '}
                <span className="text-[#7f00ff]">da sua equipe.</span>
              </h2>
              <p className="max-w-md text-lg leading-relaxed text-slate-500">
                Peça uma sessão para o time que ainda está aprendendo o sistema.
                Confirmamos o horário e conectamos você direto com um instrutor.
              </p>
            </div>

            <TrustCard />
          </div>

          {/* ── RIGHT: formulário ───────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 rounded-[32px] border border-slate-100 bg-white p-8 shadow-[0_30px_80px_-25px_rgba(15,23,42,0.15)] lg:order-2 lg:p-10"
          >
            <TrainingRequestForm />
          </motion.div>

        </div>
      </div>
    </section>
  );
}
